import { getToken } from '../authentication/authProvider';

const API = (
  process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/'
).replace(/\/$/, '');

export interface Reviewer {
  id?: string;
  name?: string;
}

async function post(path: string, body?: Record<string, unknown>): Promise<any> {
  const token = await getToken();
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`);
  }
  return res.json().catch(() => ({}));
}

/**
 * "Resend link" on a paperwork_missing board item: re-sends the family's
 * dashboard email (the same /leads/send-quote-email the lead page uses).
 */
export const resendPaperworkLink = (link: {
  quoteId: string;
  leadId?: string | null;
  email: string;
  clientName: string;
  clientId?: number;
}) => post('/leads/send-quote-email', link);

/** Send (or edit-and-send) a work item's drafted SMS. */
export const sendWorkItem = (id: string, body?: string, reviewer?: Reviewer) =>
  post(`/work-items/${id}/send`, { body, reviewer });

export const dismissWorkItem = (id: string, reviewer?: Reviewer) =>
  post(`/work-items/${id}/dismiss`, { reviewer });

/**
 * Record acceptance of a suggested reply that was sent through the funeral-feed
 * composer (the message already went out — this does NOT re-send). Closes the
 * work item and logs an approved/edited review.
 */
export const acceptWorkItem = (id: string, body: string, reviewer?: Reviewer) =>
  post(`/work-items/${id}/accept`, { body, reviewer });

/**
 * The family was replied to WITHOUT using the suggestion (a from-scratch reply).
 * Closes the item and logs a 'superseded' review — distinct from an explicit
 * dismiss/rejected — so the accept-rate denominator counts silent non-use.
 */
export const supersedeWorkItem = (id: string, body?: string, reviewer?: Reviewer) =>
  post(`/work-items/${id}/supersede`, { body, reviewer });

export interface ApplyFieldUpdate {
  dataPath: string;
  /** Edited value; omit to write the value the agent staged. */
  value?: string;
}

/**
 * Approve an `ingest` work item — write the staged field values to the case's
 * client records. `updates` = the subset the reviewer kept (with any edits); the
 * server re-derives each target row/column from the staged proposal.
 */
export const applyFields = (
  id: string,
  updates: ApplyFieldUpdate[],
  reviewer?: Reviewer,
) => post(`/work-items/${id}/apply-fields`, { updates, reviewer });

/** Manually confirm a staff work item as handled/done. */
export const confirmWorkItem = (id: string) => post(`/work-items/${id}/confirm`);

export const snoozeWorkItem = (id: string) => post(`/work-items/${id}/snooze`);
